import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';
import { fetchProjectDetail, fetchProjectSkills, fetchProjects } from '../../api/projectApi';
import MetaTagsUpdater from '../../components/MetaTagsUpdater'; // Import MetaTagsUpdater

// Helper function to unescape HTML entities
const unescapeHtml = (html) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.documentElement.textContent;
};

function ProjectDetail({ setDynamicSeo }) {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [recentProjects, setRecentProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToast } = useToast();

  useEffect(() => {
    const fetchProjectDetails = async () => {
      if (!slug) {
        setLoading(false);
        setProject(null);
        addToast('error', 'Invalid project URL. Slug is missing.');
        if (setDynamicSeo) {
          setDynamicSeo({ title: '', description: '', dynamicImage: '' });
        }
        return;
      }

      try {
        setLoading(true);

        const projectResponse = await fetchProjectDetail(slug);
        if (!projectResponse.success) {
          addToast('error', projectResponse.message || 'Failed to fetch project details.');
          setLoading(false);
          setProject(null);
          if (setDynamicSeo) {
            setDynamicSeo({ title: '', description: '', dynamicImage: '' });
          }
          return;
        }

        const fetchedProject = projectResponse.data;

        const skillsResponse = await fetchProjectSkills(fetchedProject.slug);
        if (skillsResponse.success) {
          fetchedProject.technologies = skillsResponse.data.map(skill => skill.name);
        } else {
          fetchedProject.technologies = [];
        }
        
        // Unescape HTML content for description
        const formattedProject = {
          ...fetchedProject,
          description: fetchedProject.description ? unescapeHtml(fetchedProject.description) : fetchedProject.description,
        };
        setProject(formattedProject);

        if (setDynamicSeo) {
          setDynamicSeo({
            title: fetchedProject.name,
            description: fetchedProject.excerpt,
            dynamicImage: fetchedProject.image, // Pass the image URL
          });
        }

        const recentProjectsResponse = await fetchProjects({ limit: 3, paginate: false });
        if (recentProjectsResponse.success) {
          const filteredRecent = recentProjectsResponse.data
            .filter(p => p.slug !== fetchedProject.slug)
            .slice(0, 3);
          setRecentProjects(filteredRecent);
        } else {
          setRecentProjects([]);
        }

      } catch (e) {
        console.error("Error fetching project details:", e);
        addToast('error', 'Failed to load project details. Please try again later.');
        setProject(null);
        if (setDynamicSeo) {
          setDynamicSeo({ title: '', description: '', dynamicImage: '' });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProjectDetails();
  }, [slug, addToast, setDynamicSeo]);

  return (
    <div className="pt-20 bg-bg-dark text-text-gray min-h-screen">
      <MetaTagsUpdater
        pageKey="ProjectDetail" // Use a specific pageKey for project details
        dynamicTitle={project?.name || ''}
        dynamicDescription={project?.excerpt || ''}
        dynamicImage={project?.image || ''} // Pass dynamicImage
      />
      <div className="container mx-auto px-4 py-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          {loading ? (
            <div className="bg-bg-light rounded-lg shadow-lg overflow-hidden p-8 mt-12 animate-pulse">
              <div className="w-full h-64 bg-gray-700 rounded-lg mb-8"></div>
              <div className="h-10 bg-gray-700 rounded w-3/4 mb-4"></div>
              <div className="h-4 bg-gray-700 rounded w-1/4 mb-6"></div>
              <div className="h-4 bg-gray-700 rounded w-full mb-2"></div>
              <div className="h-4 bg-gray-700 rounded w-full mb-2"></div>
              <div className="h-4 bg-gray-700 rounded w-2/3"></div>
            </div>
          ) : !project ? (
            <div className="pt-20 text-center text-text-white min-h-screen bg-bg-dark">
              <h1 className="text-4xl font-bold mb-4">Project Not Found</h1>
              <p className="text-lg">The project you are looking for does not exist or the URL is invalid.</p>
              <Link to="/projects" className="text-accent-red hover:underline mt-4 inline-block">
                Back to Projects
              </Link>
            </div>
          ) : (
            <>
              <img src={project.image} alt={project.name} className="w-full h-[500px] object-cover object-top rounded-md mb-8" />
              <h1 className="text-5xl font-bold text-text-white text-center mb-10 relative after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-20 after:h-1 after:bg-accent-red after:rounded-sm after:bottom-[-15px]">
                {project.name}
              </h1>

              <div className="bg-bg-light rounded-lg shadow-lg overflow-hidden p-8 mt-12">
                <div
                  className="rich-text-content" // Applied custom rich text styling class
                  dangerouslySetInnerHTML={{ __html: project.description || `<p>${project.excerpt}</p>` }}
                ></div>

                <div className="mb-6 mt-8">
                  <h3 className="text-2xl font-semibold text-text-white mb-3">Technologies Used:</h3>
                  <div className="flex flex-wrap gap-3">
                    {Array.isArray(project.technologies) && project.technologies.map((tech, index) => (
                      <span key={index} className="bg-accent-red/20 text-accent-red text-md px-4 py-3 rounded-full font-medium">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex justify-center gap-6 mt-8">
                  {project.live_url && (
                    <a href={project.live_url} target="_blank" rel="noopener noreferrer" className="text-text-white bg-accent-red px-6 py-3 rounded-full hover:bg-red-700 transition-colors duration-300 font-bold">
                      Live Demo
                    </a>
                  )}
                  {project.github_url && (
                    <a href={project.github_url} target="_blank" rel="noopener noreferrer" className="text-text-white bg-accent-red px-6 py-3 rounded-full hover:bg-red-700 transition-colors duration-300 font-bold">
                      GitHub Repository
                    </a>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="lg:col-span-1">
          <div className="bg-gray-800 rounded-lg shadow-lg p-6 mb-8 border border-border-subtle">
            <h3 className="text-2xl font-bold text-text-white relative after:content-[''] after:absolute after:bottom-[-4px] after:left-0 after:w-16 after:h-1 after:bg-accent-red after:rounded-sm pb-1">Recent Projects</h3>
            {loading ? (
              <ul className="space-y-4 mt-4">
                {Array.from({ length: 3 }).map((_, index) => (
                  <li key={index} className="h-16 bg-gray-700 rounded mb-4 animate-pulse"></li>
                ))}
              </ul>
            ) : recentProjects.length > 0 ? (
              <ul className="space-y-4 mt-4">
                {recentProjects.map((recentProject) => (
                  <li key={recentProject.id}>
                    <Link to={`/projects/${recentProject.slug}`} className="block text-text-gray hover:text-accent-red transition-colors duration-200">
                      <h4 className="text-lg font-semibold">{recentProject.name}</h4>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-text-gray text-sm">No recent projects found.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProjectDetail;